import { useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// material
import {
  TextField,
  Box,
  Divider,
  Autocomplete,
  Stack,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Button,
  Fade,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

// graphql
import { useQuery, useMutation } from '@apollo/client';
import { PushNotificationGQLFunction } from '../../graphql/PUSH_NOTIFICATION';

// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../people/SearchNotFound';
import { ListToolbar, ListHead, ListMoreMenu, LoadingList } from '../people';
import BasicBreadcrumb from '../../components/BasicBreadcrumb';
import EmptyList from '../people/EmptyList';

export default function SendPushNotificationForm({ theme }) {
  const { t } = useTranslation();
  const [isAllUsers, setIsAllUsers] = useState(false);
  const [isPushId, setIsPushId] = useState(false);
  const [isUser, setIsUser] = useState(true);
  const receiverRef = useRef(null);
  const [content, setContent] = useState('');
  const [subject, setSubject] = useState('');
  const [receivers, setReceivers] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const [sendPushToAllUsers, { loading: sendPushToAllUsersLoading, data: sendPushToAllUsersData }] = useMutation(
    PushNotificationGQLFunction.SEND_PUSH_TO_ALL_USERS
  );
  const [sendPushToPushIds, { loading: sendPushToPushIdLoading, data: sendPushToPushIdData }] = useMutation(
    PushNotificationGQLFunction.SEND_PUSH_TO_PUSH_ID
  );
  const [sendPushToUsers, { loading: sendPushToUsersLoading, data: sendPushToUsersData }] = useMutation(
    PushNotificationGQLFunction.SEND_PUSH_TO_USERS
  );

  const cleanForm = () => {
    setSubject('');
    setContent('');
    setReceivers([]);
  };

  const handleSubmit = () => {
    if (isAllUsers) {
      try {
        sendPushToAllUsers({ variables: { subject, content } });
        console.log('send to all users');
      } catch (error) {
        console.log(error);
      }
      cleanForm();
    }
    if (isPushId) {
      sendPushToPushIds({ variables: { pushIds: receivers, subject, content } });
      cleanForm();
    }
    if (isUser) {
      sendPushToUsers({ variables: { users: receivers, subject, content } });
      cleanForm();
    }
  };

  const handlePlaceholder = () => {
    if (isAllUsers) {
      return '';
    }
    if (isPushId) {
      return t('common.push_id');
    }
    return t('common.email');
  };

  useEffect(() => {
    if (sendPushToAllUsersLoading === false && sendPushToAllUsersData && isAllUsers) {
      if (sendPushToAllUsersData.sendPushNotificationToAll === 'success') {
        enqueueSnackbar(t('notify.sent_success'), {
          TransitionComponent: Fade,
          variant: 'success'
        });
      }
      if (sendPushToAllUsersData.sendPushNotificationToAll === 'error') {
        enqueueSnackbar(t('notify.sent_error'), {
          TransitionComponent: Fade,
          variant: 'error'
        });
      }
    } else if (sendPushToUsersLoading === false && sendPushToUsersData && isUser) {
      if (sendPushToUsersData.sendPushNotificationToUsers === 'success') {
        enqueueSnackbar(t('notify.sent_success'), {
          TransitionComponent: Fade,
          variant: 'success'
        });
      }
      if (sendPushToUsersData.sendPushNotificationToUsers === 'error') {
        enqueueSnackbar(t('notify.sent_error'), {
          TransitionComponent: Fade,
          variant: 'error'
        });
      }
    } else if (sendPushToPushIdLoading === false && sendPushToPushIdData && isPushId) {
      if (sendPushToPushIdData.sendPushNotificationToPushIds === 'success') {
        enqueueSnackbar(t('notify.sent_success'), {
          TransitionComponent: Fade,
          variant: 'success'
        });
      }
      if (sendPushToPushIdData.sendPushNotificationToPushIds === 'error') {
        enqueueSnackbar(t('notify.sent_error'), {
          TransitionComponent: Fade,
          variant: 'error'
        });
      }
    }
  }, [sendPushToAllUsersData, sendPushToUsersData, sendPushToPushIdData, t, enqueueSnackbar]);

  return (
    <Box sx={{ m: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Label variant="ghost" color={'default'}>
          {t('notify.to')}:
        </Label>
        <Autocomplete
          multiple
          id="tags-outlined"
          size="small"
          options={[]}
          getOptionLabel={(option) => option.title}
          filterSelectedOptions
          freeSolo
          fullWidth
          ref={receiverRef}
          disabled={isAllUsers}
          // loading={loading}
          value={isAllUsers ? [t('notify.all_users')] : receivers}
          onChange={(e, newValue) => {
            // console.log(newValue);
            setReceivers(newValue);
          }}
          renderTags={(values, getTagProps) =>
            // console.log('value: ', value);
            isAllUsers ? (
              <Label variant="ghost" color={'info'}>
                {t('notify.all_users')}
              </Label>
            ) : (
              values.map((value, index) => <Chip size="small" label={value} {...getTagProps({ index })} />)
            )
          }
          renderInput={(params) => (
            <TextField
              sx={{ border: 'none', '& fieldset': { border: 'none' } }}
              {...params}
              placeholder={handlePlaceholder()}
              value={isAllUsers ? t('notify.all_users') : params.value}
            />
          )}
        />
        <FormControlLabel
          sx={{ minWidth: '10rem' }}
          control={
            <Checkbox
              onChange={(e) => {
                setIsUser(e.target.checked);
                setIsPushId(false);
                setIsAllUsers(false);
                setReceivers([]);
                receiverRef.current.value = isAllUsers ? [{ title: t('notify.all_users') }] : [];
              }}
              checked={isUser}
            />
          }
          label={t('notify.selected_users')}
        />
        <FormControlLabel
          sx={{ minWidth: '7rem' }}
          control={
            <Checkbox
              onChange={(e) => {
                setIsPushId(e.target.checked);
                setIsUser(false);
                setIsAllUsers(false);
                setReceivers([]);
                receiverRef.current.value = isPushId ? [{ title: t('notify.all_users') }] : [];
              }}
            />
          }
          checked={isPushId}
          label={t('notify.push_id_label')}
        />

        <FormControlLabel
          sx={{ minWidth: '8rem' }}
          control={
            <Checkbox
              onChange={(e) => {
                setIsAllUsers(e.target.checked);
                setIsPushId(false);
                setIsUser(false);
                setReceivers([]);
                receiverRef.current.value = isAllUsers ? [{ title: t('notify.all_users') }] : [];
              }}
            />
          }
          checked={isAllUsers}
          label={t('notify.all_users')}
        />
      </Stack>
      <Divider sx={{ my: '0.5rem' }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Label variant="ghost" color={'default'}>
          {t('common.title')}:
        </Label>
        <TextField
          sx={{ border: 'none', '& fieldset': { border: 'none' } }}
          size="small"
          placeholder={t('notify.title_placeholder')}
          fullWidth
          onChange={(e) => {
            setSubject(e.target.value);
          }}
          value={subject}
        />
      </Stack>
      <Divider sx={{ my: '0.5rem' }} />
      <Label variant="ghost" color={'default'}>
        {t('common.content')}:
      </Label>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        multiline
        onChange={(e) => {
          setContent(e.target.value);
        }}
        value={content}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={'1rem'}>
        <IconButton aria-label="cancel" color="error" sx={{ borderRadius: '8px' }}>
          <Iconify icon={'eva:trash-outline'} />
        </IconButton>
        <LoadingButton
          type="submit"
          variant="contained"
          sx={{ minWidth: '10rem' }}
          onClick={handleSubmit}
          disabled={(receivers.length === 0 && !isAllUsers) || !subject || !content}>
          {t('common.send')}
        </LoadingButton>
      </Stack>
    </Box>
  );
}
